import { type Month, MONTH_ORDER, type CustomerStatementsDatesOutput } from '@egw/api'
import { WEALTH_ROUTES } from '@egw/constants'
import { titleCapitalization } from '@egw/utils'
import { MAX_DOCS_NUMBER } from '../../constants'

export interface DocumentCenterDocument {
  name: string
  rank: number | null
  url: string
}

/**
 * Processes customer statement dates and returns statement dates from the most recent year
 * formatted for display with proper sorting by year and month
 * @param data - Customer statements data
 * @param maxCount - Optional maximum number of statements to return. If not provided, returns all statements from the most recent year
 * @returns Array of formatted statement documents or undefined if no data
 */
export const getLatestStatementDates = (
  data: CustomerStatementsDatesOutput | undefined,
  maxCount?: number,
): DocumentCenterDocument[] | undefined => {
  if (!data?.statementsDates) return undefined

  // Most Recent Year already at index 0, sorting just to be sure
  const latestYearOfStatements = data.statementsDates.sort((a, b) => Number(b.year) - Number(a.year))[0]

  if (!latestYearOfStatements) return undefined

  // Recent Months are already at index 0, sorting just to be sure
  const latestYearDocsSortedByMonth = latestYearOfStatements.months.sort(
    (a, b) => MONTH_ORDER.indexOf(b) - MONTH_ORDER.indexOf(a),
  )

  // Apply slice only if maxCount is provided, otherwise return all
  const monthsToShow =
    maxCount !== undefined ? latestYearDocsSortedByMonth.slice(0, maxCount) : latestYearDocsSortedByMonth

  return monthsToShow.map((month) => ({
    name: `${titleCapitalization(month)} ${latestYearOfStatements.year ?? ''}`,
    url: `${WEALTH_ROUTES.DOCUMENTS_STATEMENTS_BY_YEAR_AND_MONTH.path}?year=${latestYearOfStatements.year ?? ''}&month=${month}`,
    rank: null,
  }))
}

/**
 * Convenience function that returns the latest 3 statement dates
 * Uses the MAX_DOCS_NUMBER constant for backward compatibility
 */
export const getLatest3StatementDates = (
  data: CustomerStatementsDatesOutput | undefined,
): DocumentCenterDocument[] | undefined => {
  return getLatestStatementDates(data, MAX_DOCS_NUMBER)
}

/**
 * Processes customer statement dates and returns statement dates from ALL years
 * formatted for display with proper sorting by year (descending) and month (descending)
 * @param data - Customer statements data
 * @returns Array of formatted statement documents from all years or undefined if no data
 */
export const getAllStatementDates = (
  data: CustomerStatementsDatesOutput | undefined,
): DocumentCenterDocument[] | undefined => {
  if (!data?.statementsDates) return undefined

  const allStatements: DocumentCenterDocument[] = []

  // Sort all years by descending order (most recent first)
  const sortedYears = data.statementsDates.sort((a, b) => Number(b.year) - Number(a.year))

  sortedYears.forEach((yearData) => {
    // Sort months within each year in descending order (most recent first)
    const sortedMonths = yearData.months.sort(
      (a, b) => MONTH_ORDER.indexOf(b as Month) - MONTH_ORDER.indexOf(a as Month),
    )

    // Create statement documents for each month in this year
    const yearStatements = sortedMonths.map((month) => ({
      name: `${titleCapitalization(month)} ${yearData.year}`,
      url: `${WEALTH_ROUTES.DOCUMENTS_STATEMENTS_BY_YEAR_AND_MONTH.path}?year=${yearData.year}&month=${month}`,
      rank: null,
    }))

    // Add all statements from this year to the results
    allStatements.push(...yearStatements)
  })

  return allStatements
}

/**
 * Sorts statement dates by year in descending order (most recent first)
 */
export const sortStatementsByYear = (
  statementsDates: Array<{ year: string; months: string[] }>,
): Array<{ year: string; months: string[] }> => {
  return statementsDates.sort((a, b) => Number(b.year) - Number(a.year))
}

/**
 * Sorts months within a year in descending order (most recent first)
/**
 * Sorts months within a year in descending order (most recent first)
 * Returns a new sorted array, does not mutate the input.
 */
export const sortMonthsByRecency = (months: string[]): string[] => {
  return [...months].sort((a, b) => MONTH_ORDER.indexOf(b as Month) - MONTH_ORDER.indexOf(a as Month))
}

export const formatStatementName = (month: string, year: string): string => {
  return `${titleCapitalization(month)} ${year}`
}

/**
 * Generates a URL for a specific statement by year and month
 */
export const generateStatementUrl = (year: string, month: string): string => {
  return `${WEALTH_ROUTES.DOCUMENTS_STATEMENTS_BY_YEAR_AND_MONTH.path}?year=${year}&month=${month}`
}
